/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { MessageService } from '@core/lib/services/message.service';
import { appConstants } from '@app/common/app-constants';
import { AppConfigService } from '@app/common/app-config.service';

const log = new Logger('RemoteArchivesService');

@Injectable({
  providedIn: 'root'
})
export class RemoteArchivesService {
  private remoteArchiveList = [];
  private isRetrievalFailed = false;
  private remoteArchiveListStatus = '';
  private loading = true;
  private isLocalArchiveOnly = true;
  private repositories;
  private checkedRepositories = [
    {
      id: appConstants.XE_SEARCH_RESPONSE_CODES.XE_SITE_TYPE_LOCAL,
      name: appConstants.XE_SEARCH_RESPONSE_CODES.XE_SITE_TYPE_LOCAL,
      type: appConstants.XE_SEARCH_RESPONSE_CODES.XE_SITE_TYPE_LOCAL
    }
  ];

  constructor(
    private dataService: ProxyDataService,
    private messageService: MessageService,
    private appConfigService: AppConfigService
  ) {}

  loadRemoteArchives(): void {
    if (_.isEmpty(this.getRemoteArchiveList())) {
      this.dataService
        .get(appConstants.appURLs.remoteArchives)
        .then(
          (remoteArchiveData: any) => {
            if (remoteArchiveData) {
              _.each(remoteArchiveData.remoteArchiveDTOList, archive => {
                this.remoteArchiveList.push(archive);
              });
              this.remoteArchiveListStatus = remoteArchiveData.remoteArchiveListStatus;
              if (this.remoteArchiveListStatus === 'Partial') {
                this.messageService.broadcast('remote.archive.list.unavailable');
              }
            }
            const remoteArchives = this.getRemoteArchiveList().map(repo => ({
              ...repo,
              availableForUser:
                repo.type === appConstants.XE_SEARCH_RESPONSE_CODES.XE_SITE_TYPE_LOCAL ||
                this.appConfigService.hasUserRemoteRepositoryAccess(repo.name)
            }));

            this.repositories = remoteArchives;
          },
          err => {
            log.error('Unable to retrieve remote archive list ' + JSON.stringify(err));
            this.isRetrievalFailed = true;
          }
        )
        .finally(() => {
          this.loading = false;
          this.messageService.broadcast('remote.archives.loading', this.loading);
        });
    }
  }

  getRemoteArchiveList() {
    return this.remoteArchiveList;
  }

  isLoading() {
    return this.loading;
  }

  isFailed() {
    return this.isRetrievalFailed;
  }

  getRemoteArchiveListStatus() {
    return this.remoteArchiveListStatus;
  }

  isPartial() {
    return this.remoteArchiveListStatus === 'Partial';
  }

  isSuccess() {
    return this.remoteArchiveListStatus === 'Success';
  }

  get isRemoteArchiveRetrievalFailed() {
    return this.isRetrievalFailed;
  }

  get allRepositories() {
    return this.repositories;
  }

  get selectedRepositories(): any[] {
    return this.checkedRepositories;
  }

  setSelectedRepositories(items) {
    this.checkedRepositories = items;
    this.checkIfLocalOnly();
  }

  isSearchLocalOnly() {
    return this.isLocalArchiveOnly;
  }

  private checkIfLocalOnly() {
    this.isLocalArchiveOnly =
      (this.isLocalRepoSelected() && this.selectedRepositories.length === 1) || this.selectedRepositories.length === 0; // We default to local search, even if nothing selected.
  }

  private isLocalRepoSelected() {
    const isRepoSelected = _.find(this.selectedRepositories, repo => {
      return repo.type === appConstants.XE_SEARCH_RESPONSE_CODES.XE_SITE_TYPE_LOCAL;
    });
    return !!isRepoSelected;
  }
}
